// Contracts between the database (supabase/migrations) and the app. Integrator-owned:
// feature work proposes changes here rather than editing it in parallel.
//
// Commands go through public.ptw_command(command, args, idempotency_key, expected_row_version).
// Reads go through public.ptw_* read RPCs. All timestamps are ISO strings (UTC offsets kept).
import type { ActivityGroup, PressureTestType } from './catalogue.ts'
import type { IdentityCommandArgs } from './identity.ts'
import type { RegistryCommandArgs } from './registry.ts'

export type Uuid = string
export type Iso = string

// -----------------------------------------------------------------------------
// Errors
// -----------------------------------------------------------------------------
export type Blocker = {
  code: string
  message: string
  rule_ids: string[]
  decision_ids: string[]
  field?: string
  parameter?: string
  role?: string
  [k: string]: unknown
}

/** SQLSTATE codes raised by the command layer (see 0001_foundation.sql). */
export type PtwErrorCode =
  | 'PW001' // PTW_BLOCKED: detail is Blocker[]
  | 'PW002' // STALE_VERSION: {expected, current}
  | 'PW003' // IDEMPOTENCY_CONFLICT
  | 'PW004' // NOT_AUTHENTICATED / ACCOUNT_INACTIVE / FORBIDDEN
  | 'PW005' // NOT_FOUND (or not visible)
  | 'PW006' // INVALID_INPUT: {field, problem}
  | 'PW007' // IMMUTABLE_RECORD
  | 'PW008' // ENVIRONMENT_FORBIDDEN / ENVIRONMENT_LOCKED
  | 'PW009' // NOT_IN_FIRST_SLICE

// -----------------------------------------------------------------------------
// Draft application content (ptw.permit_drafts.fields). Saved incomplete; checked
// at submit_permit. Everything here is the PA's input; the server derives the rest.
// -----------------------------------------------------------------------------
export type ProcedureReference = {
  identity: string // document number, e.g. "SYN-MS-0001"
  revision: string
  issuer: string
}

/**
 * One task of the application (0015 shape). Equipment is typed text (the CMMS register is
 * not complete). The pressure-test details are required when the activity group is
 * 'Pressure Testing'; the group only decides which questions are asked, never the class
 * or a limit. Every task needs its procedure reference.
 */
export type DraftTask = {
  sequence: number
  equipment_text: string | null
  description: string
  activity_group: ActivityGroup | null
  /** Required when activity_group is 'Pressure Testing'. */
  pressure_test_type: PressureTestType | null
  /** Actual medium as stated by the applicant; never inferred from the type label. */
  test_medium: string | null
  /** Required for Surface Preparation tasks; record the actual method. */
  surface_preparation_method: string | null
  applicable_procedure_reference: ProcedureReference | null
}

/** Third party or subcontractor taking part in the activity (0015, GOV-004). */
export type ThirdParty = {
  /** null = not answered; submit_permit blocks it. */
  involved: boolean | null
  /** Each must be confirmed when involved is true. */
  hm_jsa_approved: boolean
  procedures_approved: boolean
  equipment_approved: boolean
  personnel_approved: boolean
}

export type HazardControl = {
  hazard: string
  control: string
  /** null = not answered; submit_permit blocks it. */
  kind: 'standard' | 'additional' | null
}

/**
 * Isolation details on the application (0031). Kinds from ISOLATION_KINDS; the planned period
 * is when the isolation goes in and comes out; certificate_documented says whether the Isolation
 * Certificate (F08) is completed, with its number. null = not answered.
 */
export type IsolationDetails = {
  kinds: string[]
  reason: string
  planned_from: Iso | null
  planned_to: Iso | null
  certificate_documented: boolean | null
  certificate_reference: string | null
}

export type DraftFields = {
  title: string
  /** Why this class: Appendix 1 row or PA/RS assessment (CORE-017/022). */
  class_basis: string
  /**
   * SP-2329 Appendix 1 activities of the chosen class (CLASS_ACTIVITIES codes; at least one,
   * 0015). They describe the work; they never set the class or a limit. Replaces the
   * free-text class basis, which drafts saved earlier may still carry.
   */
  class_activities: string[]
  site_area_id: Uuid | null
  work_type_id: Uuid | null
  location_detail: string
  /** Required at submission (0015). */
  work_order_ref: string | null
  requested_from: Iso | null
  requested_to: Iso | null
  work_description: string
  work_boundary: string
  tools_equipment: string
  work_party_max: number | null
  nominated_ph_person_id: Uuid | null
  /** Asked for, and required, only when the start is sooner than the class notice. */
  urgency_justification: string | null
  third_party: ThirdParty
  /** The PA confirms each was verified and found adequate for the job (0015); none is pre-ticked. */
  job_readiness: {
    emergency_response_adequate: boolean
    ppe_adequate: boolean
    personnel_credentials_adequate: boolean
    equipment_credentials_adequate: boolean
  }
  tasks: DraftTask[]
  hazards: HazardControl[]
  jhsep: {
    /** null = not answered; submit_permit blocks it. */
    level: 1 | 2 | null
    reference: string
    summary: string
    mopo_reviewed: boolean
    lessons_reviewed: boolean
    site_examination_done: boolean
    /** Required at submission (0015): when the applicant visited and what they found. */
    site_examination_note: string | null
  }
  simops: {
    nearby_activities: string
    conflicts_identified: string
    mopo_consulted: boolean
  }
  gas_plan: {
    /** Must be answered explicitly before submission; null is "not answered". */
    required: boolean | null
    triggers: string[]
    species: string[]
    /** Hours between gas tests; required when gas testing is required (0015). */
    retest_interval_hours: number | null
    continuous_monitoring: boolean | null
    notes: string
  }
  /** Each must be answered; true for isolation asks for the details below (0031); true for the others selects a route not built. */
  isolation_required: boolean | null
  /** The isolation the work needs (0031; asked when isolation_required is true). */
  isolation: IsolationDetails
  hot_work: boolean | null
  confined_space_entry: boolean | null
  excavation: boolean | null
  vehicle_entry: boolean | null
  supporting_documents: { kind: string; reason: string }[]
  affected_area_ids: Uuid[]
  /** The applicant confirms the unit's work-location classification applies at the worksite. */
  location_attributes_confirmed: boolean
}

// -----------------------------------------------------------------------------
// Commands
// -----------------------------------------------------------------------------
/** Identity commands (0022) are declared in src/domain/identity.ts. */
export type CommandArgs = IdentityCommandArgs & RegistryCommandArgs & {
  create_permit_draft: { site_id: Uuid; permit_class: 'B' | 'A'; fields: Partial<DraftFields>; replaces_permit_id?: Uuid | null }
  /** Replaces the whole draft fields object. */
  revise_permit_draft: { permit_id: Uuid; fields: Partial<DraftFields> }
  submit_permit: { permit_id: Uuid }
  return_for_revision: { permit_id: Uuid; revision_id: Uuid; reason: string; change_disposition: 'minor' | 'significant' }
  /** The PA takes back a submitted, unreviewed application to correct it (0019). */
  recall_permit_application: { permit_id: Uuid; revision_id: Uuid; reason: string }
  record_meeting_review: {
    permit_id: Uuid
    revision_id: Uuid
    planning_basis: 'frozen_plan' | 'urgent_justified'
    checklist: Record<string, boolean>
    participants: { name: string; role: string }[]
    rs_pre_authorisation_visit_required: boolean
    notes?: string | null
  }
  record_pc_approval: { permit_id: Uuid; revision_id: Uuid; notes: string }
  record_oac_agreement: { permit_id: Uuid; revision_id: Uuid; affected_site_area_id: Uuid; added_controls: string }
  record_worksite_examination: { permit_id: Uuid; revision_id: Uuid; exam_kind: 'pa_rs_pre_authorisation'; reported_observed_at: Iso; findings: string }
  record_supporting_document: {
    permit_id: Uuid
    revision_id: Uuid
    requirement_id: Uuid
    record_kind: string
    external_identifier: string
    external_revision: string
    issuer: string
    reported_issued_at?: string | null
    reported_expires_at?: string | null
    scope_note?: string | null
    evidence_reference?: string | null
  }
  verify_supporting_document: { permit_id: Uuid; supporting_record_id: Uuid; findings: string }
  withdraw_supporting_document: { permit_id: Uuid; supporting_record_id: Uuid; reason: string }
  authorise_permit: { permit_id: Uuid; revision_id: Uuid; authorisation_from?: Iso | null; authorisation_to?: Iso | null }
  /** The Operations Manager's once-per-authorisation independent approval (0016). No cycle can be
   *  requested and no work can start until it is recorded. Needs expected_row_version. */
  approve_permit_om: { permit_id: Uuid; revision_id: Uuid; authorisation_id: Uuid; declaration_confirmed: boolean; notes?: string | null }
  /**
   * The permit's PA or PH asks the RS to extend the authorised period (0020), before the current
   * authorised end, to at most the policy allowance beyond the ORIGINAL end in total. Versioned.
   */
  request_extension: { permit_id: Uuid; requested_to: Iso; reason: string }
  /** The RS approves the open request: a new 'extension' authorisation becomes current; the OM approval carries over (0020). */
  approve_extension: { permit_id: Uuid; extension_request_id: Uuid; declaration_confirmed: boolean; notes?: string | null }
  /** The RS refuses the open request; the reason is required (0020). */
  refuse_extension: { permit_id: Uuid; extension_request_id: Uuid; reason: string }
  /** The requester withdraws their open request (0020). */
  withdraw_extension: { permit_id: Uuid; extension_request_id: Uuid; reason?: string | null }

  request_cycle: { permit_id: Uuid; requested_from: Iso; requested_to: Iso }
  preissue_cycle: { permit_id: Uuid; cycle_id: Uuid; checks: Record<string, boolean>; plan_reference: string; conflict_review_reference: string }
  validate_cycle: {
    permit_id: Uuid
    cycle_id: Uuid
    validation_from: Iso
    validation_to: Iso
    checks: { code: string; result: 'confirmed' | 'not_applicable' | 'prearranged'; note?: string | null }[]
    prearrangement?: { tester_person_id: Uuid; planned_at: Iso; sample_location: string; species: string[] } | null
    examination?: { reported_observed_at: Iso; findings: string } | null
    stop_resolution?: { note: string; resolving_gas_test_id?: Uuid | null } | null
    /** Class A only (0030): the validating RS stays in the area; the first-gas-test delegation when another AGT does it. */
    class_a?: { rs_remains_in_area: boolean; first_gas_test_delegation?: string | null } | null
  }
  record_gas_test: {
    permit_id: Uuid
    cycle_id: Uuid
    instrument_id: Uuid
    test_kind: string
    sample_location: string
    reported_observed_at: Iso
    measurements: { species_code: string; unit_code: string; measured_value: number; agt_declared_within_criteria?: boolean | null }[]
    notes?: string | null
  }
  record_toolbox_talk: {
    permit_id: Uuid
    cycle_id: Uuid
    giver_role: 'PH' | 'PA'
    reported_conducted_at: Iso
    content: { topics_covered: string[]; additional_hazards: string; dra_plan: string; pause_and_check_points: string }
    attendee_person_ids: Uuid[]
  }
  sign_tbt_attendance: { permit_id: Uuid; talk_id: Uuid }
  accept_cycle: { permit_id: Uuid; cycle_id: Uuid; toolbox_talk_id: Uuid; declarations: Record<string, boolean> }

  report_suspension: {
    permit_id: Uuid
    cycle_id?: Uuid | null
    reason_code: string
    reported_condition: string
    reported_observed_at: Iso
    /** 'AA' is accepted only on permits governed by the policy versions before 0016. */
    capacity: 'PH' | 'RS' | 'AA' | 'fact_reporter'
  }
  /** confirmation_role 'AA' is accepted only on permits governed by the policy versions before 0016. */
  confirm_suspension: { permit_id: Uuid; suspension_id: Uuid; confirmation_role: 'PH' | 'RS' | 'AA'; safe_state_confirmed: boolean; notes?: string | null }
  request_scope_change: { permit_id: Uuid; description: string; reporter_role: string }
  sign_ph_cancellation: {
    permit_id: Uuid
    reason_code: string
    work_complete: boolean
    worksite_condition: string
    tools_equipment_removed: boolean
    declarations: Record<string, boolean>
    /** GOV-035: required when work_complete is false - the permit on which the work continues. */
    continuation_reference?: string | null
  }
  close_out_permit: {
    permit_id: Uuid
    site_safe_confirmed: boolean
    work_status_confirmed: 'complete' | 'incomplete'
    inspection_findings: string
    operation_check?: string | null
    checks: Record<string, boolean>
    /** 0031: required when the sealed application needed isolation (ISOLATION_DISPOSITIONS); omitted otherwise. */
    isolation_disposition?: string | null
    /** The permit the isolation was transferred to, or the EPI reference; required for those two dispositions. */
    isolation_disposition_note?: string | null
  }

  /** A geographical area (site) with its first unit, under a site the caller may apply at (0015). */
  add_site: { parent_site_id: Uuid; site_name: string; unit_name: string; location_classification: string | null }
  /** A unit in a geographical area the caller may apply at (0015). */
  add_unit: { site_id: Uuid; unit_name: string; location_classification: string | null }
  /**
   * Licence register (0021, account administrators): a company-wide licence (every BadrEOR
   * location and unit; HSE Manager decision 2026-11-04) for a person on the administrator's
   * sites. expires_on is required unless expiry_not_stated is true.
   */
  register_credential_evidence: {
    person_id: Uuid
    role_code: string
    issued_on: string
    expires_on: string | null
    expiry_not_stated?: boolean
    evidence_reference: string
    issuer?: string | null
  }
  /** Withdraws a licence (append-only); effective now when effective_at is null, never in the past (0021). */
  revoke_credential: { credential_id: Uuid; reason: string; effective_at?: Iso | null }
  /** Names a unit's responsible RS and deputies, now or from a later time (0021, decision F). */
  set_unit_custodian: {
    site_area_id: Uuid
    responsible_rs_person_id: Uuid
    deputy_person_ids?: Uuid[]
    effective_from?: Iso | null
    reason?: string | null
  }
  manage_access_grant: Record<string, unknown>
  /**
   * Rule-set administration (0023; a current administer_policy grant). A candidate is made from
   * the BadrEOR operational skeleton or copied from a real (never synthetic) version.
   */
  create_policy_candidate:
    | { template: 'beor_operational'; version_label: string }
    | { base_version_id: Uuid; version_label: string; source_baseline?: string | null }
  /** Sets one value of an unfrozen candidate; an approved value names its approved source. Never synthetic. */
  set_policy_candidate_value: {
    policy_version_id: Uuid
    parameter_code: string
    value: unknown
    value_status: 'approved' | 'source_confirmed_pending_approval' | 'unresolved'
    source_reference?: string | null
    note?: string | null
    rule_ids?: string[]
    decision_ids?: string[]
  }
  /** Fixes the candidate's values and digest for review and approval. */
  freeze_policy_candidate: { policy_version_id: Uuid; declaration_confirmed: boolean }
  /**
   * Records the evidence of a human review or approval of the frozen digest. The reviewer or
   * approver is named as the signed document states; the recorder is the signed-in person.
   * The approval (after a review by a different person) makes the version approved.
   */
  record_policy_approval: {
    policy_version_id: Uuid
    approval_kind: 'review' | 'approval'
    approver_name: string
    approver_role_title: string
    approver_person_id?: Uuid | null
    approved_on: string
    document_reference: string
    document_revision: string
    effective_from?: Iso | null
    rules_digest: string
    evidence_location?: string | null
    notes?: string | null
    declaration_confirmed: boolean
  }
  /** Makes an approved, capable version the one new permits use at the chosen issuing authorities (operational only). */
  activate_policy_version: {
    policy_version_id: Uuid
    authority_ids: Uuid[]
    effective_from: Iso
    reason: string
    declaration_confirmed: boolean
  }
  /** Withdraws an approved version: no new permit uses it and the permits it governs block. */
  withdraw_policy_version: { policy_version_id: Uuid; reason: string; declaration_confirmed: boolean }
}
export type CommandName = keyof CommandArgs

/** Commands that do not require expected_row_version (append-only facts, stops). */
export const VERSIONLESS_COMMANDS: readonly CommandName[] = [
  'create_permit_draft',
  'add_site',
  'add_unit',
  'register_credential_evidence',
  'revoke_credential',
  'set_unit_custodian',
  // Rule-set administration is not permit-scoped (0023).
  'create_policy_candidate',
  'set_policy_candidate_value',
  'freeze_policy_candidate',
  'record_policy_approval',
  'activate_policy_version',
  'withdraw_policy_version',
  'record_gas_test',
  'sign_tbt_attendance',
  'report_suspension',
  'request_scope_change',
  'withdraw_supporting_document',
]

export type CommandResult = {
  command: CommandName
  request_id: Uuid
  permit_id?: Uuid
  row_version?: number
  replayed?: boolean
  [k: string]: unknown
}

// -----------------------------------------------------------------------------
// Read shapes (public.ptw_* read RPCs, implemented in 0006_reads.sql)
// -----------------------------------------------------------------------------
export type PersonRef = { id: Uuid; display_name: string; organisation: string | null }
export type AreaRef = { id: Uuid; code: string; name: string }

export type CredentialRef = {
  credential_id: Uuid
  acting_role: string
  role_code: string
  credential_kind: string
  issuer: string
  issued_on: string
  expires_on: string | null
  verification_status: string
  is_synthetic: boolean
}

export type AttestationView = {
  id: Uuid
  signer: PersonRef
  signing_role: string
  declared_capacity: string
  server_signed_at: Iso
  content_digest: string
  /** The exact signed declaration and inputs (0010, RVS-06): private.digest(content) = content_digest. */
  content?: unknown
  /** How content was canonicalised for content_digest (0010): 'jsonb-text-v1'. */
  canonicalisation_version?: string
  policy_version_id: Uuid | null
  credential: CredentialRef | null
  is_synthetic: boolean
}

export type Eligibility = {
  status: 'recorded_conditions_satisfied' | 'blocked' | 'unresolved'
  evaluated_at: Iso
  permit_id: Uuid
  revision_id: Uuid | null
  cycle_id: Uuid | null
  policy_version_id: Uuid | null
  policy_status: string | null
  synthetic: boolean
  blockers: Blocker[]
}

export type AvailableAction = {
  command: CommandName
  label: string
  role: string
  /** Pre-filled argument hints, e.g. {cycle_id}. */
  args?: Record<string, unknown>
  enabled: boolean
  reason?: string | null
}

/**
 * A sealed task row. boundary, is_pressure_test, equipment_id and procedure_evidence_refs
 * are the columns of revisions sealed before 0015; from 0015 boundary and the evidence
 * list are empty, equipment_id is null and is_pressure_test is the activity group's
 * answer (Pressure Testing).
 */
export type RevisionTask = Omit<DraftTask, 'applicable_procedure_reference'> & {
  equipment_id: Uuid | null
  boundary: string
  is_pressure_test: boolean
  procedure_evidence_refs: string[]
  id: Uuid
  equipment: { id: Uuid; tag: string; description: string } | null
  procedure_identity: string | null
  procedure_revision: string | null
  procedure_issuer: string | null
  procedure_evidence_status: 'not_applicable' | 'missing' | 'referenced'
  /** Derived at read time from verified supporting records. */
  procedure_verification_status: 'not_applicable' | 'unverified' | 'verified'
}

export type RevisionRequirement =
  | { id: Uuid; sequence: number; requirement_kind: 'supporting_document'; detail: { kind: string; reason: string } }
  | { id: Uuid; sequence: number; requirement_kind: 'oac_agreement'; detail: { site_area_id: Uuid; area?: AreaRef } }
  | {
      id: Uuid
      sequence: number
      requirement_kind: 'gas_testing'
      /** retest_interval_hours as entered (from 0015); retest_interval_minutes is the same interval in whole minutes. */
      detail: {
        triggers: string[]
        species: string[]
        retest_interval_hours?: number | null
        retest_interval_minutes: number | null
        continuous_monitoring: boolean
        notes: string
      }
    }
  | { id: Uuid; sequence: number; requirement_kind: 'worksite_examination'; detail: { exam_kind: string; basis: string } }

export type AreaClassification = {
  location_type: string
  critical_sour: boolean
  restricted: boolean
  hazardous_zone: string
  h2s_classification: string
  h2s_expected_max_ppm: number
  norm_restricted: boolean
  classification_source: string
  classification_revision: string
  classification_status: string
}

export type RevisionView = {
  id: Uuid
  revision_number: number
  sealed_at: Iso
  scope_digest: string
  permit_class: 'A' | 'B'
  site_area: AreaRef
  work_type: { id: Uuid; code: string; description: string; activity_group: string }
  area_classification: AreaClassification
  /** The sealed application exactly as signed (DraftFields shape plus server additions). */
  content: Record<string, unknown>
  tasks: RevisionTask[]
  parties: { role_code: 'PA' | 'PH'; person: PersonRef; organisation: string | null }[]
  controls: { sequence: number; hazard: string; control: string; control_kind: 'standard' | 'additional' }[]
  requirements: RevisionRequirement[]
  jhsep: {
    level: 1 | 2
    reference: string
    summary: string
    mopo_reviewed: boolean
    lessons_reviewed: boolean
    site_examination_done: boolean
    site_examination_note: string | null
  } | null
  submission: AttestationView
  supersedes_revision_id: Uuid | null
}

export type GasTestView = {
  id: Uuid
  test_kind: string
  sample_location: string
  reported_observed_at: Iso
  recorded_at: Iso
  classification: 'acceptable' | 'unsafe' | 'unresolved'
  classification_basis: string
  method_reference: string | null
  notes: string | null
  instrument: { id: Uuid; instrument_identifier: string; model: string } | null
  measurements: { species_code: string; unit_code: string; measured_value: number; agt_declared_within_criteria: boolean | null; comparison_result: string }[]
  attestation: AttestationView
}

export type ToolboxTalkView = {
  id: Uuid
  giver_role: 'PH' | 'PA'
  reported_conducted_at: Iso
  content: { topics_covered: string[]; additional_hazards: string; dra_plan: string; pause_and_check_points: string }
  giver: AttestationView
  attendees: { id: Uuid; person: PersonRef; role_in_crew: string | null; signed: AttestationView | null }[]
  complete: boolean
}

export type SuspensionView = {
  id: Uuid
  reason_code: string
  reported_condition: string
  reported_observed_at: Iso
  reporter: PersonRef | null
  reporter_capacity: string
  server_received_at: Iso
  ph_attestation: AttestationView | null
  /**
   * The formal report's own signature, PH or RS (AA before 0016) (0010, RVS-05); null for fact reports and
   * system stops. Always present in reads from 0011 on; optional only so that bundles and
   * fixtures made before it still type-check.
   */
  reporter_attestation?: AttestationView | null
  source_gas_test_id: Uuid | null
  /** confirmation_role 'AA' appears only on records made before 0016 (historic). */
  confirmations: { confirmation_role: 'PH' | 'RS' | 'AA'; safe_state_confirmed: boolean; notes: string | null; attestation: AttestationView }[]
}

/** One Operations Manager approval of one authorisation (0016). */
export type OmApprovalView = {
  id: Uuid
  revision_id: Uuid
  authorisation_id: Uuid
  /** The exact declaration the OM signed. */
  declaration: string
  notes: string | null
  attestation: AttestationView
}

/**
 * One extension request (0020) with its decision. status 'lapsed': never decided and the permit
 * was cancelled. The requester signs as PA or PH; the decision is the RS's signature (approval or
 * refusal) or the requester's (withdrawal).
 */
export type ExtensionRequestView = {
  id: Uuid
  revision_id: Uuid
  /** The authorisation current when it was asked for (the one it would extend). */
  base_authorisation_id: Uuid
  /** The permit's initial authorisation: the allowance counts from its end. */
  original_authorisation_id: Uuid
  previous_authorisation_to: Iso
  requested_to: Iso
  reason: string
  requester_role: 'PA' | 'PH'
  requested: AttestationView
  status: 'pending' | 'approved' | 'refused' | 'withdrawn' | 'lapsed'
  decision: {
    decision: 'approved' | 'refused' | 'withdrawn'
    /** Refusal reason (always given) or withdrawal reason (optional). */
    reason: string | null
    /** Approval notes (optional). */
    notes: string | null
    /** The extension authorisation an approval created. */
    authorisation_id: Uuid | null
    attestation: AttestationView
  } | null
}

/** How far the permit may still be extended (0020); null before authorisation. */
export type ExtensionAllowance = {
  /** Policy parameter, e.g. 'class_b.extension_allowance_days'. */
  parameter: string
  /** null when the policy value is not usable: every extension is then refused. */
  allowance_days: number | null
  original_authorisation_id: Uuid
  original_authorisation_to: Iso
  current_authorisation_to: Iso
  /** original_authorisation_to + allowance_days; null when the value is not usable. */
  latest_allowed_to: Iso | null
  open_request_id: Uuid | null
}

/** How the OM approval stands for display (0016). */
export type OmApprovalStatus = 'not_required' | 'pending' | 'approved'

export type CycleView = {
  id: Uuid
  cycle_number: number
  revision_id: Uuid
  authorisation_id: Uuid
  cycle_state: 'requested' | 'preissued' | 'validated' | 'accepted' | 'suspension_reported' | 'suspended' | 'ended'
  requested_from: Iso
  requested_to: Iso
  requested_by: PersonRef
  created_at: Iso
  ended_at: Iso | null
  end_reason: string | null
  replaces_cycle_id: Uuid | null
  preissue: { checks: Record<string, boolean>; plan_reference: string; conflict_review_reference: string; attestation: AttestationView } | null
  validation: {
    validation_from: Iso
    validation_to: Iso
    gas_condition: 'not_required' | 'complete' | 'prearranged'
    prearrangement: { tester_person_id: Uuid; tester?: PersonRef; planned_at: Iso; sample_location: string; species: string[] } | null
    stop_resolution: { note: string; resolving_gas_test_id: Uuid | null } | null
    checks: { check_code: string; result: string; note: string | null }[]
    attestation: AttestationView
  } | null
  examinations: { id: Uuid; exam_kind: string; reported_observed_at: Iso; findings: string; attestation: AttestationView }[]
  gas_tests: GasTestView[]
  toolbox_talks: ToolboxTalkView[]
  acceptance: { declarations: Record<string, boolean>; toolbox_talk_id: Uuid; attestation: AttestationView } | null
  suspensions: SuspensionView[]
}

/** One Class A validation's conditions (0030): presence declaration and the first gas test of the cycle. */
export type ClassAValidationConditions = {
  id: Uuid
  cycle_id: Uuid
  validation_id: Uuid
  rs_remains_in_area: boolean
  daily_examination_required: boolean
  first_gas_tester: PersonRef | null
  first_gas_test_source: 'recorded_test' | 'prearranged' | 'not_required'
  first_gas_test_delegated: boolean
  delegation_note: string | null
  attestation: AttestationView
}

/** What Class A adds to a permit (0030, private.class_a_view). */
export type ClassAView = {
  pc_approval_required: boolean
  pc_approval_recorded: boolean
  /** Worksite examination at every validation for this work location. */
  daily_examination: 'required' | 'not_required' | 'unresolved' | 'policy_blocked'
  /** Whether the validating RS may delegate the first gas test of the day; null when the rule is not usable. */
  first_test_delegation_allowed: boolean | null
  validation_conditions: ClassAValidationConditions[]
}

export type PermitBundle = {
  permit: {
    id: Uuid
    permit_number: string
    permit_class: 'A' | 'B'
    lifecycle_state: string
    row_version: number
    site: { id: Uuid; code: string; name: string }
    site_area: AreaRef
    work_type: { id: Uuid; code: string; description: string; activity_group: string }
    is_synthetic: boolean
    created_at: Iso
    created_by: PersonRef
    replaces_permit_id: Uuid | null
    current_revision_id: Uuid | null
    current_authorisation_id: Uuid | null
    current_cycle_id: Uuid | null
    title: string
  }
  policy: { version_id: Uuid; version_label: string; status: string; profile_code: string; profile_name: string }
  authority: { id: Uuid; authority_mode: string; scope_mode: string; issuer: string; approval_evidence_reference: string | null }
  draft: { draft_version: number; fields: DraftFields; updated_at: Iso; updated_by: PersonRef; based_on_revision_id: Uuid | null } | null
  revisions: RevisionView[]
  reviews: {
    id: Uuid
    revision_id: Uuid
    review_kind: 'meeting_review' | 'return_for_revision'
    outcome: 'accepted' | 'returned'
    planning_basis: string | null
    checklist: Record<string, boolean>
    participants: { name: string; role: string }[]
    reason: string | null
    change_disposition: string | null
    rs_pre_authorisation_visit_required: boolean | null
    attestation: AttestationView
  }[]
  approvals: { id: Uuid; revision_id: Uuid; approval_kind: 'pc_approval' | 'oac_agreement'; affected_site_area: AreaRef | null; added_controls: string | null; notes: string | null; attestation: AttestationView }[]
  examinations: { id: Uuid; revision_id: Uuid; cycle_id: Uuid | null; exam_kind: string; reported_observed_at: Iso; findings: string; attestation: AttestationView }[]
  supporting_records: {
    id: Uuid
    revision_id: Uuid
    requirement_id: Uuid | null
    record_kind: string
    external_identifier: string
    external_revision: string
    issuer: string
    reported_issued_at: string | null
    reported_expires_at: string | null
    scope_note: string | null
    evidence_reference: string | null
    recorded: AttestationView
    status: 'recorded' | 'verified' | 'withdrawn'
    verifications: { id: Uuid; verification_kind: 'verified' | 'withdrawn'; findings: string; attestation: AttestationView }[]
  }[]
  /**
   * Initial authorisation and RS-approved extensions (authorisation_kind 'extension', 0020). An
   * extension names the authorisation it extends; the key is missing on bundles from before 0020.
   */
  authorisations: {
    id: Uuid
    revision_id: Uuid
    authorisation_from: Iso
    authorisation_to: Iso
    authorisation_kind: string
    supersedes_authorisation_id?: Uuid | null
    attestation: AttestationView
  }[]
  /** Extension requests, oldest first (0020); missing on bundles from before 0020. */
  extension_requests?: ExtensionRequestView[]
  /** The extension allowance (0020); null before authorisation, missing on older bundles. */
  extension_allowance?: ExtensionAllowance | null
  /** Class A rules and records (0030); only on Class A permits. */
  class_a?: ClassAView | null
  /**
   * Whether the permit's policy needs the Operations Manager's approval of each authorisation
   * (0016, policy SYN-0.3). Optional only so bundles and fixtures made before 0016 still
   * type-check: missing means not required.
   */
  om_approval_required?: boolean
  /**
   * OM approvals. The current one has authorisation_id === permit.current_authorisation_id, or that
   * of an authorisation the current one extends (the approval carries over, 0020). Missing = none.
   */
  om_approvals?: OmApprovalView[]
  cycles: CycleView[]
  scope_changes: { id: Uuid; revision_id: Uuid; reporter: PersonRef; reporter_role: string; description: string; server_received_at: Iso; replacement_permit_id: Uuid | null }[]
  cancellation: {
    id: Uuid
    reason_code: string
    work_complete: boolean
    worksite_condition: string
    tools_equipment_removed: boolean
    declarations: Record<string, boolean>
    continuation_reference: string | null
    isolation_disposition: string
    attestation: AttestationView
  } | null
  closeout: {
    id: Uuid
    site_safe_confirmed: boolean
    work_status_confirmed: 'complete' | 'incomplete'
    inspection_findings: string
    operation_check: string | null
    checks: Record<string, boolean>
    isolation_disposition: string
    /** 0031: the permit or EPI reference behind a transferred or EPI disposition. Missing before 0031. */
    isolation_disposition_note?: string | null
    attestation: AttestationView
  } | null
  archive: {
    job: { id: Uuid; status: string; attempt_count: number; last_error: string | null; bundle_digest: string; retention_status: string; requested_at: Iso; completed_at: Iso | null }
    artifacts: { artifact_kind: string; storage_bucket: string; storage_path: string; sha256: string; byte_count: number; render_template_version: string }[]
  } | null
  events: {
    sequence: number
    event_kind: string
    actor_kind: string
    actor: PersonRef | null
    server_recorded_at: Iso
    revision_id: Uuid | null
    cycle_id: Uuid | null
    prior_row_version: number | null
    new_row_version: number | null
    details: Record<string, unknown>
  }[]
  eligibility: Eligibility
  viewer: { person: PersonRef; roles_on_permit: string[]; available_actions: AvailableAction[] }
  generated_at: Iso
}

/** The canonical archive bundle: PermitBundle without viewer-, time- and archive-dependent parts. */
export type ArchiveBundle = Omit<PermitBundle, 'viewer' | 'eligibility' | 'generated_at' | 'archive' | 'draft' | 'permit'> & {
  /** lifecycle_state and row_version change after closeout, so the archive leaves them out. */
  permit: Omit<PermitBundle['permit'], 'lifecycle_state' | 'row_version'>
  bundle_version: string
}

export type PermitSummary = {
  id: Uuid
  permit_number: string
  permit_class: 'A' | 'B'
  lifecycle_state: string
  title: string
  site: { id: Uuid; name: string }
  site_area: AreaRef
  row_version: number
  current_cycle: { id: Uuid; cycle_number: number; cycle_state: string; validation_to: Iso | null } | null
  eligibility_status: Eligibility['status']
  my_roles: string[]
  /** Enabled command names for the caller now (from private.available_actions). */
  my_pending_actions: string[]
  /** Operations Manager approval of the current authorisation (0016); missing on older servers. */
  om_approval?: OmApprovalStatus
  updated_at: Iso
  is_synthetic: boolean
}

export type Me = {
  account_id: Uuid
  person: PersonRef
  environment_kind: 'test' | 'synthetic_demo' | 'staging' | 'operational'
  is_synthetic: boolean
  sites: { id: Uuid; code: string; name: string; capabilities: string[] }[]
  credentials: {
    credential_id: Uuid
    role_code: string
    credential_kind: string
    issued_on: string
    expires_on: string | null
    status: string
    scopes: { scope_mode: string; site_area: AreaRef | null; work_type_code: string | null }[]
  }[]
  demo: { sandbox_code: string; persona_code: string } | null
  server_time: Iso
  /** Software capabilities granted without a site, e.g. administer_accounts (0012). */
  global_capabilities?: string[]
}

/** Help & Learning self-study state kept with the person (0012). Never a qualification. */
export type LearningProgressState = {
  schemaVersion: 2
  contentId: string
  contentVersion: string
  mode: string
  selectedRole: string
  currentStepId: string
  readStepIds: string[]
  practisedStepIds: string[]
  hasStarted: boolean
}

export type LearningProgressRecord = { progress: LearningProgressState; started_at?: Iso; updated_at: Iso }

export type LearningOverview = {
  content_id: string
  content_version: string
  generated_at: Iso
  people: {
    person_id: Uuid
    display_name: string
    organisation: string | null
    persona_code: string | null
    /** Role codes of the person's current credentials on the administrator's sites (0014). */
    roles: string[]
    progress: LearningProgressState | null
    started_at: Iso | null
    updated_at: Iso | null
    /** Most recent progress on another edition of the same tutorial, or null (0014). */
    older_edition: { content_version: string; updated_at: Iso; read_count: number; practised_count: number } | null
  }[]
}

/** Licence register status words (0021): from private.credential_status at server time. */
export type LicenceStatus = 'current' | 'expired' | 'revoked' | 'not_yet_valid' | 'expiry_not_stated' | 'unverified' | 'missing'

/** A unit's responsible RS and deputies: the only people who give its OAC agreement (0021, decision F). */
export type UnitCustodian = {
  assignment_id: Uuid
  responsible: PersonRef
  deputies: PersonRef[]
  effective_from: Iso
  assigned_at: Iso
  assigned_by: string
  reason: string | null
}

/** public.ptw_licence_register() (0021, account administrators; their sites only). */
export type LicenceRegister = {
  generated_at: Iso
  sites: {
    id: Uuid
    code: string
    name: string
    /** Each unit's responsible RS and deputies now, and the next assignment set ahead (decision F). */
    units: { id: Uuid; code: string; name: string; custodian: UnitCustodian | null; scheduled: UnitCustodian | null }[]
  }[]
  people: { person_id: Uuid; display_name: string; organisation: string | null; persona_code: string | null; is_me: boolean }[]
  licences: {
    credential_id: Uuid
    person_id: Uuid
    role_code: string
    credential_kind: string
    issuer: string
    issued_on: string
    expires_on: string | null
    expiry_basis: 'stated' | 'not_stated'
    evidence_reference: string | null
    verification_status: string
    is_synthetic: boolean
    status: LicenceStatus
    /** Valid at every location of the company (scope_mode 'organisation', or a legacy explicit_global). */
    company_wide: boolean
    /** The company-wide scope and any older unit scopes inside the administrator's sites. */
    scopes: { scope_mode: string; organisation: { id: Uuid; name: string } | null; site_area: AreaRef | null; work_type_code: string | null }[]
    recorded: { at: Iso; by: string | null } | null
    withdrawal: { effective_at: Iso; reason: string; recorded_at: Iso; by: string | null } | null
    is_mine: boolean
  }[]
}

export type Registry = {
  sites: {
    id: Uuid
    code: string
    name: string
    authority: { authority_mode: string; policy_version_id: Uuid; issuer: string } | null
    /** Units. location_classification is the unit's work-location classification (0015; LOCATION_CLASSIFICATIONS code). */
    areas: (AreaRef & AreaClassification & { location_classification?: string | null; location_classification_label?: string | null })[]
  }[]
  equipment: { id: Uuid; site_area_id: Uuid; tag: string; description: string }[]
  work_types: { id: Uuid; code: string; description: string; activity_group: string }[]
  /**
   * roles = role codes of the person's current credentials (licences, AGT certificate, appointments).
   * credentials = the same current credentials with their scopes inside the caller's sites (0015);
   * used only for early warnings, the server's role check decides.
   */
  people: {
    id: Uuid
    display_name: string
    organisation: string | null
    roles: string[]
    credentials?: { role_code: string; scopes: { scope_mode: string; site_area_id: Uuid | null; work_type_id: Uuid | null }[] }[]
  }[]
  gas_instruments: { id: Uuid; site_id: Uuid | null; instrument_identifier: string; model: string; status: string; verification_effective_to: string | null }[]
}

export type PolicyView = {
  version: {
    id: Uuid
    version_label: string
    status: string
    source_baseline: string
    rules_digest: string | null
    effective_from: Iso | null
    /** 0023 */
    effective_to?: Iso | null
    approval_evidence_reference?: string | null
  }
  profile: { code: string; name: string; permit_family: string; system_of_record_mode: string; is_synthetic?: boolean }
  parameters: {
    parameter_code: string
    value: unknown
    value_status: 'source_confirmed_pending_approval' | 'synthetic_fixture' | 'approved' | 'unresolved'
    rule_ids: string[]
    decision_ids: string[]
    note: string | null
    /** The approved source of the value (0023); null on values written before. */
    source_reference?: string | null
  }[]
} & Partial<PolicyEvidence> & { environment_kind?: string; can_administer?: boolean }

/** Lifecycle, approval evidence, activations and the capability check of a rule version (0023). */
export type PolicyEvidence = {
  lifecycle: 'candidate' | 'frozen' | 'approved' | 'scheduled' | 'active' | 'superseded' | 'withdrawn' | 'synthetic'
  freeze: { rules_digest: string; content_digest: string; parameter_count: number; frozen_at: Iso; frozen_by: string } | null
  approvals: {
    id: Uuid
    approval_kind: 'review' | 'approval'
    approver_name: string
    approver_role_title: string
    approved_on: string
    document_reference: string
    document_revision: string
    effective_from: Iso | null
    rules_digest: string
    evidence_location: string | null
    notes: string | null
    recorded_at: Iso
    recorded_by: string
  }[]
  activations: {
    id: Uuid
    authority_id: Uuid
    site_name: string
    unit_name: string | null
    scope_mode: string
    effective_from: Iso
    reason: string
    activated_at: Iso
    activated_by: string
    in_force: boolean
    scheduled: boolean
  }[]
  capability_check: CapabilityCheck
}

export type CapabilityCheck = {
  ok: boolean
  blockers: Blocker[]
  warnings: Blocker[]
  gas_model?: string | null
  implemented?: ImplementedCapabilities
}

export type ImplementedCapabilities = {
  permit_classes: string[]
  routes: { code: string; label: string; required_parameters: string[] }[]
  unimplemented_routes: string[]
}

/** public.ptw_policy_admin (0023; administer_policy only). */
export type PolicyAdminView = {
  environment_kind: string
  server_time: Iso
  implemented: ImplementedCapabilities
  versions: (PolicyEvidence & {
    id: Uuid
    version_label: string
    status: string
    profile_code: string
    profile_name: string
    profile_is_synthetic: boolean
    effective_from: Iso | null
    effective_to: Iso | null
    rules_digest: string | null
    approval_evidence_reference: string | null
    created_at: Iso
    parameter_count: number
    unresolved_count: number
    activation_blockers: Blocker[]
  })[]
  authorities: {
    id: Uuid
    site_id: Uuid
    site_name: string
    unit_name: string | null
    scope_mode: string
    issuer: string
    authority_mode: string
    effective_from: Iso
    effective_to: Iso | null
    revoked: boolean
    is_synthetic: boolean
    active_version: { id: Uuid; version_label: string; status: string; effective_from: Iso } | null
    scheduled: { version_id: Uuid; version_label: string; effective_from: Iso }[]
  }[]
}

export type BoardEntry = {
  permit_id: Uuid
  permit_number: string
  title: string
  permit_class: 'A' | 'B'
  site_area: AreaRef
  lifecycle_state: string
  cycle: { id: Uuid; cycle_number: number; cycle_state: string; validation_from: Iso | null; validation_to: Iso | null } | null
  ph: PersonRef | null
  /** The person who validated the current cycle (the key keeps its historic name). */
  aa: PersonRef | null
  /** The role that person validated as: 'RS' from 0016, 'AA' on older records (0016). */
  validator_role?: 'RS' | 'AA' | null
  /** Operations Manager approval of the current authorisation (0016). */
  om_approval?: OmApprovalStatus
  /** The current authorised end (0020). */
  authorisation_to?: Iso | null
  /** The current authorisation is an RS-approved extension (0020). */
  extended?: boolean
  /** An extension request is waiting for the RS (0020). */
  extension_pending?: boolean
  eligibility: Eligibility
}

export type DemoPersona = {
  persona_code: string
  display_name: string
  description: string
  fixture_id: string | null
  person_id: Uuid
  organisation: string | null
  bound: boolean
  credentials: { role_code: string; credential_kind: string; expires_on: string | null; revoked: boolean }[]
}
